import { Controller } from "@hotwired/stimulus"

// Time Build Controller — boundary drawing, crew management, clock-in demo, save wizard
export default class extends Controller {
  static targets = [
    "map", "stepIndicator", "boundaryStep", "boundaryPanel",
    "crewStep", "crewSheet", "crewList", "crewNameInput", "crewRoleInput",
    "addBtn", "saveBar", "saveBtn", "saveCount", "mapCanvas",
    "venueName", "toast", "toastText", "crewCount",
    "modalCrewBadge",
    "clockinOverlay", "clockinVideo", "clockinFallback", "clockinCamera",
    "clockinName", "clockinRole", "clockinTime", "clockinDate",
    "clockinLocation", "clockinConfirmBtn",
    "geofenceLabel", "clockinHint", "saveHint"
  ]

  static values = {
    lat: { type: Number, default: 51.5074 },
    lng: { type: Number, default: -0.1278 },
    zoom: { type: Number, default: 19 },
    venueName: { type: String, default: 'Your venue' },
    saveStateUrl: String,
    completeUrl: String,
    signedIn: Boolean
  }

  connect() {
    this.currentStep = 'boundary'
    this.crewMembers = []
    this.crewMarkers = []
    this.boundaryCoords = null
    this.boundary = null
    this.dashedBorder = null
    this.clockedInMembers = new Set()
    this._clockinTimeInterval = null
    this._clockinStream = null
    this._clockinMemberId = null

    if (typeof google !== 'undefined' && google.maps) {
      this.initMap()
    } else {
      document.addEventListener('google:maps:ready', () => {
        this.initMap()
      }, { once: true })
    }
  }

  disconnect() {
    clearInterval(this._clockinTimeInterval)
    this._stopWebcam()
  }

  initMap() {
    const mapOptions = {
      center: { lat: this.latValue, lng: this.lngValue },
      zoom: this.zoomValue,
      mapTypeId: 'satellite',
      disableDefaultUI: true,
      zoomControl: true,
      zoomControlOptions: {
        position: google.maps.ControlPosition.RIGHT_CENTER
      },
      gestureHandling: 'greedy'
    }

    this.map = new google.maps.Map(this.mapTarget, mapOptions)

    // Draw initial boundary after map is ready
    google.maps.event.addListenerOnce(this.map, 'idle', () => {
      this.drawBoundary()
      this._drawGeofenceArrow()

      // Keep boundary centered as user pans the map
      this._centerListener = this.map.addListener('center_changed', () => {
        if (this.currentStep !== 'boundary' || !this.boundary) return
        this._repositionBoundaryToCenter()
      })
    })
  }

  // Draw an editable circle boundary around the venue center
  drawBoundary() {
    const center = this.map.getCenter()

    if (this.boundary) {
      this.boundary.setMap(null)
    }
    if (this.dashedBorder) {
      this.dashedBorder.setMap(null)
    }

    this.boundary = new google.maps.Circle({
      center: center,
      radius: 50,
      strokeColor: '#ec4899',
      strokeOpacity: 0,
      strokeWeight: 0,
      fillColor: '#ec4899',
      fillOpacity: 0.12,
      editable: true,
      draggable: true,
      map: this.map
    })

    this._drawDashedCircleBorder()

    // Sync dashed border when circle is edited or dragged
    google.maps.event.addListener(this.boundary, 'radius_changed', () => { this._drawDashedCircleBorder() })
    google.maps.event.addListener(this.boundary, 'center_changed', () => { this._drawDashedCircleBorder() })

    this.boundaryCoords = this._getCircleCoords()
  }

  // Generate circle points for the dashed polyline border
  _getCircleCoords() {
    const center = this.boundary.getCenter()
    const radius = this.boundary.getRadius()
    const points = []
    const numPoints = 72
    for (let i = 0; i <= numPoints; i++) {
      const angle = (i / numPoints) * 2 * Math.PI
      const lat = center.lat() + (radius / 111320) * Math.cos(angle)
      const lng = center.lng() + (radius / (111320 * Math.cos(center.lat() * Math.PI / 180))) * Math.sin(angle)
      points.push({ lat, lng })
    }
    return points
  }

  // Draw a dashed polyline border around the circle
  _drawDashedCircleBorder() {
    if (this.dashedBorder) {
      this.dashedBorder.setMap(null)
    }
    const points = this._getCircleCoords()
    this.dashedBorder = new google.maps.Polyline({
      path: points,
      strokeOpacity: 0,
      icons: [{
        icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, strokeColor: '#ec4899', strokeWeight: 4, scale: 7 },
        offset: '0',
        repeat: '24px'
      }],
      map: this.map
    })
  }

  // Reposition boundary circle to stay centered on the map
  _repositionBoundaryToCenter() {
    if (!this.boundary) return
    this.boundary.setCenter(this.map.getCenter())
  }

  // Draw white curly arrow from geo-fence label to boundary
  _drawGeofenceArrow() {
    if (!this.hasGeofenceLabelTarget) return
    const step = this.boundaryStepTarget
    if (!step) return

    // Remove any previous arrow
    const old = step.querySelector('.time-build__geofence-arrow')
    if (old) old.remove()

    const stepRect = step.getBoundingClientRect()
    const labelRect = this.geofenceLabelTarget.getBoundingClientRect()

    // Arrow starts from bottom-left of label
    const x1 = labelRect.left - stepRect.left + 20
    const y1 = labelRect.bottom - stepRect.top + 4

    // Arrow ends at center of map (where boundary is)
    const x2 = stepRect.width / 2
    const y2 = stepRect.height / 2

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    svg.setAttribute('class', 'time-build__geofence-arrow')
    svg.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:17;overflow:visible;'
    step.appendChild(svg)

    const dx = x2 - x1, dy = y2 - y1
    const dist = Math.sqrt(dx * dx + dy * dy)
    const perpX = -dy / dist, perpY = dx / dist
    const bulge = dist * 0.25
    const cp1x = x1 + dx * 0.3 + perpX * bulge
    const cp1y = y1 + dy * 0.3 + perpY * bulge
    const cp2x = x1 + dx * 0.7 + perpX * bulge * 0.4
    const cp2y = y1 + dy * 0.7 + perpY * bulge * 0.4

    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
    path.setAttribute('d', `M${x1},${y1} C${cp1x},${cp1y} ${cp2x},${cp2y} ${x2},${y2}`)
    path.setAttribute('stroke', '#ffffff')
    path.setAttribute('stroke-width', '3')
    path.setAttribute('fill', 'none')
    path.setAttribute('stroke-linecap', 'round')
    svg.appendChild(path)

    const angle = Math.atan2(y2 - cp2y, x2 - cp2x)
    const aLen = 14, aAng = 0.45
    const pts = [
      x2 - aLen * Math.cos(angle - aAng), y2 - aLen * Math.sin(angle - aAng),
      x2, y2,
      x2 - aLen * Math.cos(angle + aAng), y2 - aLen * Math.sin(angle + aAng)
    ].map(v => Math.round(v)).join(',')
    const arrow = document.createElementNS('http://www.w3.org/2000/svg', 'polygon')
    arrow.setAttribute('points', pts)
    arrow.setAttribute('fill', '#ffffff')
    svg.appendChild(arrow)
  }

  // Reset polygon to default position
  redraw() {
    this.drawBoundary()
    this.showToast('Boundary redrawn')
  }

  // Confirm boundary and transition to crew step
  confirmBoundary() {
    // Save current circle boundary
    const center = this.boundary.getCenter()
    this.boundaryCoords = this._getCircleCoords()
    this.boundaryCenter = { lat: center.lat(), lng: center.lng() }
    this.boundaryRadius = this.boundary.getRadius()

    // Make circle non-editable and stop following center
    this.boundary.setEditable(false)
    this.boundary.setDraggable(false)
    if (this._centerListener) {
      google.maps.event.removeListener(this._centerListener)
      this._centerListener = null
    }

    // Transition to crew step
    this.currentStep = 'crew'

    // Hide boundary step, show crew step
    if (this.hasBoundaryStepTarget) {
      this.boundaryStepTarget.classList.remove('time-build__step--active')
    }
    if (this.hasCrewStepTarget) {
      this.crewStepTarget.classList.add('time-build__step--active')
    }

    // Move the map canvas into the crew step (it persists across steps)
    if (this.hasMapCanvasTarget && this.hasCrewStepTarget) {
      this.crewStepTarget.insertBefore(this.mapCanvasTarget, this.crewStepTarget.firstChild)
      // Shrink map to top half
      this.mapCanvasTarget.classList.add('time-build__map-canvas--shrunk')
      // Trigger map resize
      google.maps.event.trigger(this.map, 'resize')
    }

    // Show crew sheet with animation
    setTimeout(() => {
      if (this.hasCrewSheetTarget) {
        this.crewSheetTarget.classList.add('time-build__crew-sheet--visible')
      }
      if (this.hasSaveBarTarget) {
        this.saveBarTarget.style.display = 'flex'
      }
    }, 100)

    // Resize again once the canvas has finished shrinking, otherwise the map
    // keeps the tiles it painted at full height and shows grey bands
    setTimeout(() => {
      if (this.map) google.maps.event.trigger(this.map, 'resize')
    }, 500)

    // Focus crew name input
    setTimeout(() => {
      if (this.hasCrewNameInputTarget) {
        this.crewNameInputTarget.focus()
      }
    }, 500)

    this.showToast('Boundary saved')
  }

  // Add a crew member
  addCrew() {
    const name = this.crewNameInputTarget.value.trim()
    if (!name) {
      this.crewNameInputTarget.focus()
      return
    }

    const role = this.hasCrewRoleInputTarget ? this.crewRoleInputTarget.value.trim() : ''
    const initial = name.charAt(0).toUpperCase()
    const id = Date.now()

    // Add to array
    this.crewMembers.push({ id, name, role, initial })

    // Drop pin on map
    this.addCrewMarker(name, initial)

    // Add to crew list
    this.renderCrewItem({ id, name, role, initial })

    // Clear inputs
    this.crewNameInputTarget.value = ''
    if (this.hasCrewRoleInputTarget) this.crewRoleInputTarget.value = ''
    this.crewNameInputTarget.focus()

    // Update save bar
    this.updateSaveBar()

    // Update crew count in top bar
    this.updateCrewCount()

    this.showToast(`${name} added`)

    // Show clock-in hint after first crew member
    if (this.crewMembers.length === 1 && this.hasClockinHintTarget) {
      this.clockinHintTarget.classList.remove('d-none')
    }
  }

  // Handle Enter key in crew inputs
  crewInputKeydown(event) {
    if (event.key === 'Enter') {
      event.preventDefault()
      this.addCrew()
    }
  }

  // Add a marker for a crew member on the map (inside the boundary circle)
  addCrewMarker(name, initial) {
    // Place within the boundary circle using polar coordinates
    const bCenter = this.boundary ? this.boundary.getCenter() : this.map.getCenter()
    const bRadius = this.boundary ? this.boundary.getRadius() : 50
    // Random point inside circle: sqrt for uniform distribution, cap at 80% of radius
    const angle = Math.random() * 2 * Math.PI
    const dist = Math.sqrt(Math.random()) * bRadius * 0.8
    const lat = bCenter.lat() + (dist / 111320) * Math.cos(angle)
    const lng = bCenter.lng() + (dist / (111320 * Math.cos(bCenter.lat() * Math.PI / 180))) * Math.sin(angle)

    const marker = new google.maps.Marker({
      position: { lat, lng },
      map: this.map,
      title: name,
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 14,
        fillColor: '#f97316',
        fillOpacity: 1,
        strokeColor: '#FFFFFF',
        strokeWeight: 3
      },
      label: {
        text: initial,
        color: '#FFFFFF',
        fontSize: '12px',
        fontWeight: '700'
      }
    })

    this.crewMarkers.push(marker)
  }

  // Render a crew item in the list
  renderCrewItem({ id, name, role, initial }) {
    const item = document.createElement('div')
    item.classList.add('time-build__crew-item')
    item.dataset.crewId = id
    item.innerHTML = `
      <div class="time-build__crew-item-info">
        <div class="time-build__crew-item-avatar">${initial}</div>
        <div>
          <div class="time-build__crew-item-name">${this.escapeHtml(name)}</div>
          ${role ? `<div class="time-build__crew-item-role">${this.escapeHtml(role)}</div>` : ''}
        </div>
      </div>
      <div class="time-build__crew-item-actions">
        <button class="time-build__crew-clockin-btn" type="button" data-action="click->time-build#openClockIn" data-crew-id="${id}">
          <i class="fas fa-clock me-1"></i> Clock in
        </button>
        <span class="time-build__crew-clockedin-badge d-none" data-clockedin-badge="${id}">
          <i class="fas fa-check-circle me-1"></i>
          <span data-clockedin-time="${id}"></span>
        </span>
        <button class="time-build__crew-item-remove" type="button" data-action="click->time-build#removeCrew" data-crew-id="${id}">
          <i class="fas fa-times"></i>
        </button>
      </div>
    `
    this.crewListTarget.appendChild(item)
  }

  // Remove a crew member
  removeCrew(event) {
    const id = parseInt(event.currentTarget.dataset.crewId)
    const index = this.crewMembers.findIndex(m => m.id === id)

    if (index > -1) {
      const member = this.crewMembers[index]
      this.crewMembers.splice(index, 1)

      // Remove marker
      if (this.crewMarkers[index]) {
        this.crewMarkers[index].setMap(null)
        this.crewMarkers.splice(index, 1)
      }

      // Remove DOM element
      const item = this.crewListTarget.querySelector(`[data-crew-id="${id}"]`)
      if (item) item.remove()

      // Clean up clocked-in state
      this.clockedInMembers.delete(id)

      this.updateSaveBar()
      this.updateCrewCount()
      this.showToast(`${member.name} removed`)
    }
  }

  // ── Clock-in Demo ──

  // Triggered by clicking the "Try clocking in!" hint — opens clock-in for first crew member
  tryClockIn() {
    if (this.crewMembers.length === 0) return
    const first = this.crewMembers[0]
    if (this.clockedInMembers.has(first.id)) return

    this._clockinMemberId = first.id
    this.clockinNameTarget.textContent = first.name
    this.clockinRoleTarget.textContent = first.role || ''
    this.clockinOverlayTarget.classList.remove('d-none')
    this._updateClockInTime()
    this._clockinTimeInterval = setInterval(() => this._updateClockInTime(), 1000)
    this._requestGeolocation()
    this._startWebcam()

    // Hide the hint
    if (this.hasClockinHintTarget) {
      this.clockinHintTarget.classList.add('d-none')
    }
  }

  openClockIn(event) {
    const id = parseInt(event.currentTarget.dataset.crewId)
    const member = this.crewMembers.find(m => m.id === id)
    if (!member) return

    this._clockinMemberId = id

    // Populate overlay
    this.clockinNameTarget.textContent = member.name
    this.clockinRoleTarget.textContent = member.role || ''

    // Show overlay
    this.clockinOverlayTarget.classList.remove('d-none')

    // Start live clock
    this._updateClockInTime()
    this._clockinTimeInterval = setInterval(() => this._updateClockInTime(), 1000)

    // Request geolocation
    this._requestGeolocation()

    // Start webcam
    this._startWebcam()
  }

  closeClockIn() {
    this.clockinOverlayTarget.classList.add('d-none')
    clearInterval(this._clockinTimeInterval)
    this._stopWebcam()
    this._clockinMemberId = null
  }

  confirmClockIn() {
    const id = this._clockinMemberId
    if (!id) return

    const now = new Date()
    const timeStr = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })

    // Mark as clocked in
    this.clockedInMembers.add(id)

    // Update the crew list item
    const item = this.crewListTarget.querySelector(`[data-crew-id="${id}"]`)
    if (item) {
      const clockinBtn = item.querySelector('.time-build__crew-clockin-btn')
      const badge = item.querySelector(`[data-clockedin-badge="${id}"]`)
      const badgeTime = item.querySelector(`[data-clockedin-time="${id}"]`)

      if (clockinBtn) clockinBtn.classList.add('d-none')
      if (badge) badge.classList.remove('d-none')
      if (badgeTime) badgeTime.textContent = `Clocked in ${timeStr}`
    }

    // Update marker color to green
    const memberIndex = this.crewMembers.findIndex(m => m.id === id)
    if (memberIndex > -1 && this.crewMarkers[memberIndex]) {
      this.crewMarkers[memberIndex].setIcon({
        path: google.maps.SymbolPath.CIRCLE,
        scale: 14,
        fillColor: '#22c55e',
        fillOpacity: 1,
        strokeColor: '#FFFFFF',
        strokeWeight: 3
      })
    }

    // Close overlay
    this.closeClockIn()

    const member = this.crewMembers.find(m => m.id === id)
    this.showToast(`${member?.name || 'Crew member'} clocked in at ${timeStr}`)

    // Hide clock-in hint, show save hint after first clock-in
    if (this.hasClockinHintTarget) {
      this.clockinHintTarget.classList.add('d-none')
    }
    if (this.clockedInMembers.size === 1 && this.hasSaveHintTarget) {
      this.saveHintTarget.classList.remove('d-none')
    }
  }

  _updateClockInTime() {
    const now = new Date()
    this.clockinTimeTarget.textContent = now.toLocaleTimeString([], {
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    })
    this.clockinDateTarget.textContent = now.toLocaleDateString([], {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
    })
  }

  _requestGeolocation() {
    this.clockinLocationTarget.textContent = 'Detecting location...'

    if (!navigator.geolocation) {
      this.clockinLocationTarget.textContent = 'Geolocation not available'
      return
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords
        this.clockinLocationTarget.textContent =
          `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`
      },
      () => {
        // Use venue coords as fallback
        this.clockinLocationTarget.textContent =
          `${this.latValue.toFixed(5)}, ${this.lngValue.toFixed(5)} (venue)`
      }
    )
  }

  async _startWebcam() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: 320, height: 320 }
      })
      this._clockinStream = stream
      this.clockinVideoTarget.srcObject = stream
      this.clockinVideoTarget.classList.remove('d-none')
      this.clockinFallbackTarget.classList.add('d-none')
    } catch {
      // Webcam denied or unavailable — show fallback avatar
      this.clockinVideoTarget.classList.add('d-none')
      this.clockinFallbackTarget.classList.remove('d-none')
    }
  }

  _stopWebcam() {
    if (this._clockinStream) {
      this._clockinStream.getTracks().forEach(track => track.stop())
      this._clockinStream = null
    }
    if (this.hasClockiVideoTarget) {
      this.clockinVideoTarget.srcObject = null
    }
  }

  // ── Save + Navigation ──

  // Update save bar state
  updateSaveBar() {
    const count = this.crewMembers.length

    if (count > 0) {
      this.saveBarTarget.classList.remove('time-build__save-bar--locked')
      this.saveBarTarget.classList.add('time-build__save-bar--active')
      this.saveBtnTarget.disabled = false
      this.saveCountTarget.textContent = `${count} crew member${count === 1 ? '' : 's'}`
    } else {
      this.saveBarTarget.classList.add('time-build__save-bar--locked')
      this.saveBarTarget.classList.remove('time-build__save-bar--active')
      this.saveBtnTarget.disabled = true
      this.saveCountTarget.textContent = 'Add at least 1 crew member'
    }
  }

  // Update crew count in top bar
  updateCrewCount() {
    if (this.hasCrewCountTarget) {
      const count = this.crewMembers.length
      this.crewCountTarget.textContent = count > 0 ? `· ${count} crew` : ''
    }
  }

  // Open save modal — POST data to server first
  async openSaveModal() {
    // Hide save hint
    if (this.hasSaveHintTarget) {
      this.saveHintTarget.classList.add('d-none')
    }

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

      await fetch(this.saveStateUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          boundary: this.boundaryCoords,
          boundary_center: this.boundaryCenter,
          boundary_radius: this.boundaryRadius,
          crew: this.crewMembers.map(m => ({ name: m.name, role: m.role }))
        })
      })

      // Arrived via the demo's magic link: already has an account, so no email to ask for
      if (this.signedInValue) {
        window.location.href = this.completeUrlValue
        return
      }

      // Update modal badge
      if (this.hasModalCrewBadgeTarget) {
        this.modalCrewBadgeTarget.textContent = `${this.crewMembers.length} crew added`
      }

      // Open the signup modal
      const signupModal = this.element.querySelector('[data-controller="signup-modal"]')
      if (signupModal) {
        const signupController = this.application.getControllerForElementAndIdentifier(
          signupModal, 'signup-modal'
        )
        if (signupController) {
          signupController.open()
        }
      }
    } catch (error) {
      this.showToast('Error saving. Please try again.')
    }
  }

  // Navigate back
  goBack() {
    if (this.currentStep === 'crew') {
      // Go back to boundary step
      this.currentStep = 'boundary'

      // Hide crew sheet
      if (this.hasCrewSheetTarget) {
        this.crewSheetTarget.classList.remove('time-build__crew-sheet--visible')
      }
      if (this.hasSaveBarTarget) {
        this.saveBarTarget.style.display = 'none'
      }

      // Un-shrink map
      if (this.hasMapCanvasTarget) {
        this.mapCanvasTarget.classList.remove('time-build__map-canvas--shrunk')
      }

      // Move map back to boundary step
      if (this.hasMapCanvasTarget && this.hasBoundaryStepTarget) {
        this.boundaryStepTarget.insertBefore(this.mapCanvasTarget, this.boundaryStepTarget.querySelector('.time-build__boundary-panel'))
      }

      // Show boundary step, hide crew step
      if (this.hasCrewStepTarget) {
        this.crewStepTarget.classList.remove('time-build__step--active')
      }
      if (this.hasBoundaryStepTarget) {
        this.boundaryStepTarget.classList.add('time-build__step--active')
      }

      // Re-enable circle editing
      if (this.boundary) {
        this.boundary.setEditable(true)
        this.boundary.setDraggable(true)
      }

      // Trigger map resize
      if (this.map) {
        google.maps.event.trigger(this.map, 'resize')
      }
    } else {
      // Go back to home page
      window.location.href = '/?product=time'
    }
  }

  // Show toast notification
  showToast(message) {
    if (!this.hasToastTarget) return

    this.toastTextTarget.textContent = message
    this.toastTarget.classList.remove('d-none')

    clearTimeout(this._toastTimeout)
    this._toastTimeout = setTimeout(() => {
      this.toastTarget.classList.add('d-none')
    }, 2500)
  }

  // Escape HTML to prevent XSS
  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
